import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { Dialog, DialogTitle, TextField } from "@mui/material";
import { Order, stableSort, getComparator } from "../ui/Tables";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { useEffect, useState } from "react";
import { Loading } from "../ui/Loading";
import { uiStore } from "../uiStore";
import { CardsData } from "../prenotazione/CardsData";
import { EuroFormatter } from "../utils/EuroFormatter";
import { Transazioni } from "./Transazioni";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface HeadCell {
  disablePadding: boolean;
  id: keyof CardsData;
  label: string;
  numeric: boolean;
  key: string;
  filter: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    numeric: false,
    disablePadding: false,
    label: "",
    key: "id",
    filter: false,
  },
  {
    id: "codiceConto",
    numeric: false,
    disablePadding: false,
    label: "Codice conto",
    key: "codiceConto",
    filter: true,
  },
  {
    id: "codice",
    numeric: false,
    disablePadding: false,
    label: "Codice card",
    key: "codice",
    filter: true,
  },
  {
    id: "importoResiduo",
    numeric: true,
    disablePadding: false,
    label: "Saldo",
    key: "saldo",
    filter: true,
  },
  {
    id: "cognome",
    numeric: false,
    disablePadding: false,
    label: "Cognome",
    key: "cognome",
    filter: true,
  },
  {
    id: "nome",
    numeric: false,
    disablePadding: false,
    label: "Nome",
    key: "nome",
    filter: true,
  },
  {
    id: "cf",
    numeric: false,
    disablePadding: false,
    label: "Codice fiscale",
    key: "cf",
    filter: true,
  },
  {
    id: "email",
    numeric: false,
    disablePadding: false,
    label: "Email",
    key: "email",
    filter: true,
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof CardsData) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  onFilter: (event: any, property: keyof CardsData, filter: string | number) => void;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, onFilter } = props;
  const createSortHandler = (property: keyof CardsData) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };
  const createFilterHandler = (property: keyof CardsData) => (event: any) => {
    onFilter(event, property, event.target.value);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.key ? headCell.key : headCell.id}>
            {headCell.filter && (
              <TextField label="" variant="standard" onChange={createFilterHandler(headCell.id)} />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function GestioneConti() {
  const { isLoading, data } = useQuery<{ cards: CardsData[] }, AxiosError>(
    ["cards/findAllPrenota"],
    () => axios.get(API_URL + "cards/findAllPrenota").then((res) => res.data)
  );

  const rows = data?.cards ?? [];

  const [conto, setConto] = useState<CardsData | undefined>();
  const [open, setOpen] = useState(false);

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof CardsData>("codiceConto");
  const [page, setPage] = React.useState(0);
  const dense = oraPrenotaStore((state) => state.tabelleCompatte);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof CardsData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [filters, setFilters] = useState({});
  const handleFilterRequest = (
    event: React.MouseEvent<unknown>,
    property: keyof CardsData,
    filter: string | number
  ) => {
    setFilters({
      ...filters,
      [property]: filter,
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => {
    setTitle("Conti");
  }, [setTitle]);

  const handleClick = (conto: CardsData) => () => {
    setConto(conto);
    setOpen(true);
  };

  if (isLoading) return <Loading />;

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer>
        <Table aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            onFilter={handleFilterRequest}
          />
          <TableBody classes={{ root: "table-body" }}>
            {stableSort(rows, getComparator(order, orderBy), filters)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: CardsData, index) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>
                      <IconButton sx={{ p: 0 }} onClick={handleClick(row)}>
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>

                    <TableCell>{row.codiceConto}</TableCell>
                    <TableCell>{row.codice}</TableCell>
                    <TableCell align="right">{EuroFormatter.format(row.importoResiduo)}</TableCell>
                    <TableCell>{row.cognome}</TableCell>
                    <TableCell>{row.nome}</TableCell>
                    <TableCell>{row.cf}</TableCell>
                    <TableCell>{row.email}</TableCell>
                  </TableRow>
                );
              })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        {conto && (
          <>
            <DialogTitle>
              Conto: {conto.codiceConto}
              {" - "}
              {conto.cognome} {conto.nome} {" - "}
              {EuroFormatter.format(conto.importoResiduo)}
            </DialogTitle>
            <Transazioni idConto={conto?.idConto} close={() => setOpen(false)} conto={conto} />
          </>
        )}
      </Dialog>
    </Box>
  );
}
